<script lang="ts" setup>
import { computed } from 'vue'
import { PaymentMethodEnum, formatDiscountLabel } from '../constants'
import paypalIcon from '@/assets/paypal.png'
import stripeIcon from '@/assets/stripe.svg'
import paypalDarkIcon from '@/assets/paypal-dark.png'
import stripeDarkIcon from '@/assets/stripe-dark.svg'

interface PaymentMethod {
  value: number
  label: string
  icon: string
  originalMethod?: any
}

// props / emit / 过滤映射 / 折扣角标 与默认组件完全等价,只换外观
const props = withDefaults(defineProps<{
  modelValue?: any
  availableMethods: any[]
  variant?: 'default' | 'large'
  dark?: boolean
  align?: 'center' | 'start'
  transparent?: boolean
}>(), {
  modelValue: undefined,
  variant: 'default',
  dark: false,
  align: 'center',
  transparent: false,
})

defineEmits<{
  (e: 'update:model-value', value: number): void
}>()

// 代码内仅有 PAYPAL / STRIPE 两个渠道(PAYMENT_METHOD_PAY_TYPES → SUBSCRIBE_PAY_TYPE.PAYPAL=6 / STRIPE=5)
const allPaymentMethods: PaymentMethod[] = [
  { value: PaymentMethodEnum.PAYPAL, label: 'PayPal', icon: paypalIcon },
  { value: PaymentMethodEnum.STRIPE, label: 'Stripe', icon: stripeIcon },
]

// 保持接口返回顺序
const paymentMethods = computed<PaymentMethod[]>(() => {
  const methods = props.availableMethods || []
  return methods
    .map((m) => {
      const methodConfig = allPaymentMethods.find(config => Number(config.value) === Number(m.paymentMethod))
      return methodConfig ? { ...methodConfig, originalMethod: m } : null
    })
    .filter(Boolean) as PaymentMethod[]
})

const resolvedPaymentMethods = computed(() =>
  paymentMethods.value.map(m => ({
    ...m,
    icon: props.dark
      ? (m.value === PaymentMethodEnum.PAYPAL ? paypalDarkIcon : stripeDarkIcon)
      : m.icon,
  })),
)
</script>

<template>
  <!-- default 变体(弹窗场景)保持与默认组件一致的胶囊样式 -->
  <div v-if="variant === 'default'" class="nm-pm-pill" :class="{ dark: props.dark }">
    <div
      v-for="method in resolvedPaymentMethods"
      :key="method.value"
      class="nm-pm-pill-btn"
      :class="{ active: Number(props.modelValue) === Number(method.value) }"
      @click="$emit('update:model-value', method.value)"
    >
      <img :src="method.icon" :alt="method.label" class="nm-pm-pill-icon">
    </div>
  </div>

  <!-- large 变体(付费墙 / 充值 / 订阅):h46 r14 双按钮 -->
  <div v-else class="nm-pm-row" :class="{ dark: props.dark, start: props.align === 'start' }">
    <div
      v-for="method in resolvedPaymentMethods"
      :key="method.value"
      class="nm-pm-btn"
      :class="{
        'is-paypal': method.value === PaymentMethodEnum.PAYPAL,
        'is-active': Number(props.modelValue) === Number(method.value),
      }"
      @click="$emit('update:model-value', method.value)"
    >
      <!-- PayPal:紫图标 + 紫文字。若后续合规要求官方素材,把这两行换成
           <img :src="method.icon" alt="PayPal" class="nm-pm-official"> 即可,其余不动 -->
      <template v-if="method.value === PaymentMethodEnum.PAYPAL">
        <i-lucide-circle-dollar-sign class="nm-pm-paypal-icon" />
        <span class="nm-pm-paypal-text">PayPal</span>
      </template>

      <!-- Stripe 聚合:apple + google + card(仅为 Stripe 渠道的视觉表达,不新增渠道) -->
      <template v-else>
        <i-lucide-apple class="nm-pm-agg-icon" />
        <i-ph-google-logo class="nm-pm-agg-icon" />
        <i-lucide-credit-card class="nm-pm-agg-icon" />
      </template>

      <div v-if="formatDiscountLabel(method?.originalMethod?.maxDiscountRate)" class="nm-pm-discount">
        {{ formatDiscountLabel(method?.originalMethod?.maxDiscountRate) }}
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ---------- large ---------- */
/* 不使用 flex gap(项目 scan:flex-gap 兼容性约束),改用相邻兄弟 margin */
.nm-pm-row {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 20px 0 0 0;
  box-sizing: border-box;
  background: transparent;
}

.nm-pm-row.start {
  justify-content: flex-start;
}

.nm-pm-btn {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  box-sizing: border-box;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.nm-pm-btn + .nm-pm-btn {
  margin-left: 12px;
}

/* PayPal:purple-soft 底 */
.nm-pm-btn.is-paypal {
  background: #F3EDFE;
  border: 1px solid transparent;
}

/* 聚合按钮:白底 + 描边 */
.nm-pm-btn:not(.is-paypal) {
  background: #FFFFFF;
  border: 1px solid #E5D8EA;
}

.nm-pm-btn.is-active {
  border: 1.5px solid #F0509B;
}

.nm-pm-paypal-icon {
  width: 17px;
  height: 17px;
  color: #7C4DE8;
}

.nm-pm-paypal-text {
  margin-left: 6px;
  font-size: 14px;
  font-weight: 800;
  line-height: 18px;
  color: #7C4DE8;
}

.nm-pm-official {
  height: 22px;
  object-fit: contain;
}

.nm-pm-agg-icon {
  width: 18px;
  height: 18px;
  color: #2B1B34;
}

.nm-pm-agg-icon + .nm-pm-agg-icon {
  margin-left: 12px;
}

.nm-pm-discount {
  position: absolute;
  top: -8px;
  left: -3px;
  padding: 0 6px;
  border-radius: 14px 0 10px 0;
  background: linear-gradient(90deg, #F0509B 0%, #9B5CF0 100%);
  color: #FFFFFF;
  font-size: 10px;
  font-weight: 800;
  line-height: 16px;
  white-space: nowrap;
  z-index: 10;
}

/* 夜间皮肤兜底(阅读器深色主题时) */
.nm-pm-row.dark .nm-pm-btn.is-paypal {
  background: rgba(124, 77, 232, 0.22);
}

.nm-pm-row.dark .nm-pm-btn:not(.is-paypal) {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.16);
}

.nm-pm-row.dark .nm-pm-paypal-icon,
.nm-pm-row.dark .nm-pm-paypal-text {
  color: #C4A8FF;
}

.nm-pm-row.dark .nm-pm-agg-icon {
  color: #FFFFFF;
}

/* ---------- default(弹窗胶囊) ---------- */
.nm-pm-pill {
  display: flex;
  padding: 4px;
  margin: 16px auto 8px;
  min-width: 200px;
  width: fit-content;
  background: #FDEFF6;
  border-radius: 999px;
}

.nm-pm-pill-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 32px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.nm-pm-pill-btn.active {
  background: #FFFFFF;
  box-shadow: 0 2px 8px rgba(124, 77, 232, 0.08);
}

.nm-pm-pill-icon {
  height: 24px;
  min-width: 60px;
  object-fit: contain;
}

.nm-pm-pill.dark {
  background: rgba(255, 255, 255, 0.08);
}

.nm-pm-pill.dark .nm-pm-pill-btn.active {
  background: rgba(255, 255, 255, 0.16);
  box-shadow: none;
}
</style>
